// The Google dashboard's display-currency conversion — PURE + tested.
//
// ⚠️⚠️ IT CONVERTS THE METRIC BLOCK, NOT THE FORMATTER, and that is the whole
// design. Every money surface on this dashboard — the KPI tiles, the tree's money
// columns, the campaign-type breakdown, the widgets, the trend series — already
// renders through `formatMetric` off a `GoogleAdsMetrics`. Rewrite that block
// once into a single display-currency and every one of them converts with no
// change of its own. Patching each formatter instead would have been six chances
// to miss one.
//
// ⚠️⚠️ A MONEY RATIO IS RECOMPUTED, NEVER CONVERTED-THEN-SUMMED. `avgCpc` arrives
// as a ratio PER CURRENCY (that currency's cost ÷ its clicks); converting each
// and adding them would produce "the AUD CPC plus the NZD CPC in AUD", which is
// not a CPC of anything. So the money TOTALS are converted and summed, and every
// ratio is then re-derived from the converted total over the (already
// all-currency) count — exactly the summed-numerator-over-summed-denominator rule
// `computeMetrics` applies natively. The formulas below MIRROR that function; if
// one changes, both must.
//
// ⚠️ ROAS IS A 'multiple', AND IT STILL WORKS: value ÷ cost with BOTH sides
// converted into the same currency is the same ratio in any currency, so the
// blended figure is meaningful. It could not be blended natively (an AUD
// numerator over an NZD denominator means nothing), which is exactly why it was
// per-currency before.

import {
  isMoneyMetric,
  metricDef,
} from '../../lib/googleAdsMetrics';
import { isNativeDisplay, toDisplayCurrency } from '../../lib/reporting/currencyConversion';
import type { FxRateSnapshot } from '../../types/fxRates';
import type {
  CurrencyAmount,
  GoogleAdsMetricKey,
  GoogleAdsMetrics,
} from '../../types/googleAdsReporting';

const r2 = (n: number): number => Math.round(n * 100) / 100;
const ratio = (num: number, den: number): number => (den > 0 ? num / den : 0);

/** One money stat, converted and blended into the display currency. */
function blend(
  amounts: CurrencyAmount[],
  display: string,
  snapshot: FxRateSnapshot | null | undefined,
): { amounts: CurrencyAmount[]; total: number } {
  // ⚠️ An EMPTY stat stays empty rather than becoming a zero entry — "we have no
  // measurement here" renders as "—", and a 0 would be a claim.
  if (amounts.length === 0) return { amounts: [], total: 0 };
  const converted = toDisplayCurrency(amounts, display, snapshot);
  if (!converted) return { amounts, total: 0 };
  // Nothing could be converted → keep the natives rather than assert a 0.
  if (converted.unconverted.length === amounts.length) return { amounts, total: 0 };
  return {
    amounts: [{ currency: converted.currency, amount: r2(converted.amount) }],
    total: converted.amount,
  };
}

/**
 * Rewrite a metric block into ONE display currency.
 *
 * Returns the block UNCHANGED in Native mode (and with no snapshot), which is
 * what makes this safe to call unconditionally at every render site.
 */
export function convertGoogleMetrics(
  metrics: GoogleAdsMetrics | undefined,
  display: string,
  snapshot: FxRateSnapshot | null | undefined,
): GoogleAdsMetrics | undefined {
  if (!metrics) return metrics;
  if (isNativeDisplay(display) || !snapshot) return metrics;

  const cost = blend(metrics.cost, display, snapshot);
  const value = blend(metrics.conversionsValue, display, snapshot);

  // The counts are ALREADY all-currency scalars (computeMetrics flattens them),
  // so they are the correct denominators for a blended ratio as they stand.
  const { clicks, impressions, conversions } = metrics;

  const one = (amount: number): CurrencyAmount[] => [{ currency: display, amount: r2(amount) }];
  // A ratio is only meaningful where the numerator survived conversion.
  const derived = (amount: number): CurrencyAmount[] =>
    cost.amounts.length === 0 ? [] : one(amount);

  return {
    ...metrics,
    cost: cost.amounts,
    conversionsValue: value.amounts,
    // Re-derived, NOT converted-and-summed. See the header.
    avgCpc: derived(ratio(cost.total, clicks)),
    avgCpm: derived(ratio(cost.total, impressions) * 1000),
    costPerConversion: derived(ratio(cost.total, conversions)),
    valuePerConversion: value.amounts.length === 0 ? [] : one(ratio(value.total, conversions)),
    // Both sides in the same currency → a meaningful blended multiple.
    roas:
      value.amounts.length === 0 || cost.amounts.length === 0
        ? []
        : one(ratio(value.total, cost.total)),
  };
}

/**
 * The currency a money metric is displayed in — the display currency when one is
 * active, otherwise the caller's native pick.
 */
export function displayCurrencyFor(
  key: GoogleAdsMetricKey,
  display: string,
  nativeCurrency: string | null,
): string | null {
  if (!isMoneyMetric(key) && metricDef(key)?.kind !== 'multiple') return null;
  return isNativeDisplay(display) ? nativeCurrency : display;
}

/** Convert one plain amount carried alongside its own currency (search-term rows). */
export function convertRowAmount(
  amount: number | null,
  from: string,
  display: string,
  snapshot: FxRateSnapshot | null | undefined,
): { amount: number | null; currency: string } {
  if (amount === null || isNativeDisplay(display) || !snapshot) {
    return { amount, currency: from };
  }
  const converted = toDisplayCurrency([{ currency: from, amount }], display, snapshot);
  // ⚠️ No rate → the NATIVE figure under its NATIVE marker, not a converted-looking
  // number. A row that can't be converted must still read truthfully.
  if (!converted || converted.unconverted.length > 0) return { amount, currency: from };
  return { amount: converted.amount, currency: display };
}
